import { Navigate } from "react-router-dom";
import { useMemo, useState, useEffect } from "react";
import { useFiscalStore } from "@/store/fiscal-store";
import { reprocessNfe } from "@/lib/nfe-backend";
import { fmtBRL, fmtNum } from "@/lib/format";
import type { Severity } from "@/lib/fiscal-types";
import { Button } from "@/components/ui/button";
import { Wand2, RotateCcw, Cloud, CloudOff, CheckCircle2, Loader2, Search, Target, ShieldCheck, Activity, Database, ArrowLeft, ArrowRight, Zap, History } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

const PAGE = 50;

type ReprocessStatus = "idle" | "loading" | "done" | "error";

export default function DivergenciasPage() {
  const { divergencias, dashboard, notas, applyAutoCorrections } = useFiscalStore();
  const [grav, setGrav] = useState<"all" | Severity>("all");
  const [page, setPage] = useState(0);
  const [reprocessStatus, setReprocessStatus] = useState<ReprocessStatus>("idle");
  const [reprocessMsg, setReprocessMsg] = useState("");
  const [busca, setBusca] = useState("");

  useEffect(() => { document.title = "Divergências — DataGrid XML"; }, []);

  const totalCorrigiveis = divergencias.length;

  const handleCorrigir = async () => {
    const { corrigidas } = applyAutoCorrections();
    if (corrigidas === 0 && divergencias.length === 0) {
      toast.info("Nenhuma divergência para corrigir.");
      return;
    }

    toast.success(
      `${corrigidas.toLocaleString("pt-BR")} divergência(s) corrigidas localmente.`,
      { description: "Dashboard e notas atualizados com os valores declarados." },
    );
    setPage(0);

    if (divergencias.length > 0) {
      const chaves = divergencias.map((d) => d.chave).filter(Boolean) as string[];
      if (!chaves.length) return;

      setReprocessStatus("loading");
      setReprocessMsg("Iniciando Pipeline de Reprocessamento Backend...");

      const result = await reprocessNfe({ chaves });

      if (result.ok) {
        setReprocessStatus("done");
        setReprocessMsg(
          `Pipeline Concluído: ${result.corrigidas ?? 0} Correções Aplicadas no Servidor.`
        );
        if ((result.erros ?? 0) > 0) {
          toast.warning(`${result.erros} erro(s) no reprocessamento backend.`, {
            description: result.detalhes_erros?.join("; "),
          });
        }
      } else {
        setReprocessStatus("error");
        setReprocessMsg(`Falha Crítica no Backend: ${result.error ?? "erro desconhecido"}.`);
      }
    }
  };

  const filtered = useMemo(
    () => {
      let list = grav === "all" ? divergencias : divergencias.filter((d) => d.gravidade === grav);
      if (busca.trim()) {
        const q = busca.trim().toLowerCase();
        list = list.filter((d) =>
          d.chave?.toLowerCase().includes(q) ||
          d.campo?.toLowerCase().includes(q) ||
          d.tipo?.toLowerCase().includes(q)
        );
      }
      return list;
    },
    [divergencias, grav, busca],
  );

  // Removido redirecionamento automático para permitir acesso livre conforme solicitado.
  // if (!dashboard && notas.length === 0) return <Navigate to="/upload" replace />;

  const slice = filtered.slice(page * PAGE, (page + 1) * PAGE);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE));

  const counts = {
    ALTA:  divergencias.filter((d) => d.gravidade === "ALTA").length,
    MEDIA: divergencias.filter((d) => d.gravidade === "MEDIA").length,
    BAIXA: divergencias.filter((d) => d.gravidade === "BAIXA").length,
  };

  return (
    <div className="p-4 lg:p-6 max-w-[1600px] mx-auto space-y-6 animate-in fade-in duration-1000">
      
      {/* Forensic Auditor Header */}
      <header className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 border-l-4 border-primary pl-8 py-4 mb-10 bg-white/[0.02] glass-technical border-y-0 border-r-0 rounded-r-3xl">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Target className="size-3 text-primary animate-pulse" />
            <span className="text-[10px] font-black uppercase tracking-[0.4em] text-primary">Data Reconciliation Protocol</span>
          </div>
          <h1 className="text-5xl font-black tracking-tighter leading-none mb-2">
            Hub de <span className="text-primary">Divergências</span>
          </h1>
          <p className="text-[12px] text-muted-foreground font-black uppercase tracking-[0.1em] mt-2 flex items-center gap-6 opacity-70">
            <span className="flex items-center gap-2 uppercase tabular-nums font-black"><Activity className="size-3.5 text-red-500" /> {fmtNum(divergencias.length)} CONFLITOS DETECTADOS</span>
            <span className="flex items-center gap-2 uppercase tabular-nums font-black text-emerald-500"><ShieldCheck className="size-3.5" /> {fmtNum(totalCorrigiveis)} RESOLVÍVEIS VIA ENGINE</span>
          </p>
        </div>

        <div className="flex items-center gap-4">
          <Button
            size="lg"
            onClick={handleCorrigir}
            disabled={totalCorrigiveis === 0 || reprocessStatus === "loading"}
            className="h-16 rounded-2xl bg-primary text-white hover:bg-primary/90 transition-all font-black text-[10px] uppercase tracking-[0.2em] px-8 shadow-2xl shadow-primary/30 group"
          >
            {reprocessStatus === "loading" ? (
              <><Loader2 className="size-5 animate-spin mr-3" /> PIPELINE EM EXECUÇÃO...</>
            ) : (
              <><Wand2 className="size-5 mr-3 group-hover:rotate-12 transition-transform" /> RESOLVER DIVERGÊNCIAS ({fmtNum(totalCorrigiveis)})</>
            )}
          </Button>
        </div>
      </header>

      {/* Reprocessing Status Hub */}
      {reprocessStatus !== "idle" && (
        <div className={cn(
          "glass-technical p-6 flex items-center gap-6 border-2 shadow-2xl animate-in slide-in-from-top-4 duration-500",
          reprocessStatus === "loading" ? "border-sky-500/30 bg-sky-500/[0.03]" :
          reprocessStatus === "done"    ? "border-emerald-500/30 bg-emerald-500/[0.03]" :
          "border-red-500/30 bg-red-500/[0.03]"
        )}>
          <div className={cn("size-12 rounded-2xl flex items-center justify-center shadow-xl", 
            reprocessStatus === "loading" ? "bg-sky-500 text-white animate-pulse" :
            reprocessStatus === "done"    ? "bg-emerald-500 text-white" :
            "bg-red-500 text-white"
          )}>
            {reprocessStatus === "loading" && <Cloud className="size-6" />}
            {reprocessStatus === "done"    && <CheckCircle2 className="size-6" />}
            {reprocessStatus === "error"   && <CloudOff className="size-6" />}
          </div>
          <div className="flex-1">
             <div className="text-[10px] font-black uppercase tracking-[0.2em] opacity-40 mb-1">Status do Processamento</div>
             <div className="text-sm font-black uppercase tracking-tight">{reprocessMsg}</div>
          </div>
          <button onClick={() => setReprocessStatus("idle")} className="size-8 rounded-xl hover:bg-foreground/5 flex items-center justify-center opacity-30 transition-all">✕</button>
        </div>
      )}

      {/* Technical Pipeline Info */}
      <div className="glass-card p-6 border-primary/5 bg-primary/[0.02] flex items-center gap-4 relative overflow-hidden group">
        <div className="absolute top-0 right-0 p-2 opacity-5">
           <Zap className="size-24 text-primary" />
        </div>
        <div className="size-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
           <RotateCcw className="size-5" />
        </div>
        <div className="text-[10px] font-black text-muted-foreground uppercase leading-relaxed tracking-widest max-w-4xl relative z-10">
          O protocolo executa: <span className="text-primary">CORE CORRECTION (LOCAL)</span> + <span className="text-primary">BACKEND SYNC (SERVER)</span>. 
          Este fluxo revalida o somatório do <code className="bg-background/50 px-1 py-0.5 rounded border border-foreground/5 text-primary">vNF</code> e sincroniza os estados de inspeção sem risco de colisão de dados.
        </div>
      </div>

      {/* Control & Matrix Hub */}
      <div className="glass-card p-6 grid grid-cols-1 xl:grid-cols-12 gap-4 items-center border-primary/5 bg-white/[0.01]">
        <div className="xl:col-span-8 flex h-14 glass-technical border-foreground/5 rounded-2xl p-1.5 bg-white/5 w-fit">
          {(["all", "ALTA", "MEDIA", "BAIXA"] as const).map((g) => (
            <button
              key={g}
              onClick={() => { setGrav(g); setPage(0); }}
              className={cn(
                "px-8 h-full text-[10px] font-black uppercase tracking-[0.2em] rounded-xl transition-all",
                grav === g ? "bg-primary text-white shadow-xl shadow-primary/30" : "text-muted-foreground hover:bg-white/5"
              )}
            >
              {g === "all" ? `TODAS (${fmtNum(divergencias.length)})` : `${g} (${fmtNum(counts[g])})`}
            </button>
          ))}
        </div>
        <div className="xl:col-span-4 relative group">
          <Search className="size-4 absolute left-5 top-1/2 -translate-y-1/2 text-muted-foreground group-focus-within:text-primary transition-colors" />
          <Input
            placeholder="Filtrar por Chave, Campo ou Tipo..."
            value={busca}
            onChange={(e) => { setBusca(e.target.value); setPage(0); }}
            className="input-premium w-full pl-14 h-14 rounded-2xl bg-white/5 border-foreground/5 text-xs font-black uppercase tracking-tight"
          />
        </div>
      </div>

      {/* Forensic Reconciliation Grid */}
      <div className="glass-card overflow-hidden border-primary/5">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-muted/10 text-muted-foreground border-b border-foreground/5">
                <th className="p-5 text-[9px] font-black uppercase tracking-[0.3em] border-r border-foreground/5">Tipo Divergência</th>
                <th className="p-5 text-[9px] font-black uppercase tracking-[0.3em] border-r border-foreground/5">Protocolo Chave</th>
                <th className="p-5 text-[9px] font-black uppercase tracking-[0.3em] border-r border-foreground/5">TAG/Campo</th>
                <th className="p-5 text-[9px] font-black uppercase tracking-[0.3em] text-right border-r border-foreground/5">Valor Engine</th>
                <th className="p-5 text-[9px] font-black uppercase tracking-[0.3em] text-right border-r border-foreground/5">Valor Declarado</th>
                <th className="p-5 text-[9px] font-black uppercase tracking-[0.3em] text-right border-r border-foreground/5">Variação (Δ)</th>
                <th className="p-5 text-[9px] font-black uppercase tracking-[0.3em] border-r border-foreground/5 text-center">Gravidade</th>
                <th className="p-5 text-[9px] font-black uppercase tracking-[0.3em] text-center">Protocol Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-foreground/[0.03]">
              {slice.map((d, i) => (
                <tr key={d.chave || `div-${i}`} className="hover:bg-primary/[0.04] transition-all group">
                  <td className="p-5 border-r border-foreground/[0.03] font-black text-[10px] text-primary uppercase tracking-tight">{d.tipo}</td>
                  <td className="p-5 border-r border-foreground/[0.03] font-mono text-[9px] text-muted-foreground/60 group-hover:text-foreground transition-colors uppercase">{d.chave || "—"}</td>
                  <td className="p-5 border-r border-foreground/[0.03] font-black text-xs uppercase tracking-tighter">{d.campo}</td>
                  <td className="p-5 border-r border-foreground/[0.03] text-right tabular-nums text-muted-foreground/40 font-bold">{fmtBRL(d.valorCalculado)}</td>
                  <td className="p-5 border-r border-foreground/[0.03] text-right tabular-nums text-foreground font-black text-sm">{fmtBRL(d.valorDeclarado)}</td>
                  <td className={cn("p-5 border-r border-foreground/[0.03] text-right tabular-nums font-black text-sm", d.diferenca < 0 ? "text-red-500" : "text-amber-500")}>
                    {fmtBRL(d.diferenca)}
                  </td>
                  <td className="p-5 border-r border-foreground/[0.03]">
                    <div className="flex justify-center">
                       <span className={cn("text-[9px] px-3 py-1 rounded-xl font-black uppercase tracking-[0.2em] border shadow-lg",
                         d.gravidade === "ALTA" ? "bg-red-500/10 text-red-500 border-red-500/20" :
                         d.gravidade === "MEDIA" ? "bg-amber-500/10 text-amber-500 border-amber-500/20" :
                         "bg-emerald-500/10 text-emerald-500 border-emerald-500/20")}>{d.gravidade}</span>
                    </div>
                  </td>
                  <td className="p-5 text-center">
                    <span className={cn("text-[9px] px-3 py-1 rounded-xl font-black uppercase tracking-[0.2em] border",
                      d.status === "CRITICO" ? "bg-red-500/10 text-red-500 border-red-500/20" : "bg-primary/10 text-primary border-primary/20")}>
                      {d.status}
                    </span>
                  </td>
                </tr>
              ))}
              {slice.length === 0 && (
                <tr>
                  <td colSpan={8} className="p-32 text-center">
                    <div className="flex flex-col items-center justify-center gap-6 opacity-10 animate-pulse">
                      <ShieldCheck className="size-20" />
                      <span className="text-xl font-black uppercase tracking-[0.4em]">Base Consolidada</span>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        
        {/* Advanced Pagination Hub */}
        <div className="flex flex-col sm:flex-row items-center justify-between p-6 bg-muted/10 border-t border-primary/10">
          <div className="flex items-center gap-4">
            <div className="size-10 rounded-xl glass-technical border-primary/20 flex items-center justify-center text-primary">
               <History className="size-5" />
            </div>
            <span className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground/60">
              Conflitos <span className="text-foreground">{(page * PAGE) + 1}—{Math.min((page + 1) * PAGE, filtered.length)}</span> de {fmtNum(filtered.length)}
            </span>
          </div>

          <div className="flex items-center gap-6 mt-4 sm:mt-0">
            <div className="flex flex-col items-end">
               <span className="text-[8px] font-black text-primary uppercase tracking-widest opacity-40">Navegação Auditoria</span>
               <span className="text-[10px] font-black text-primary uppercase tracking-widest tabular-nums">Página {page + 1} de {totalPages}</span>
            </div>
            <div className="flex gap-2">
              <button disabled={page === 0} onClick={() => setPage((p) => p - 1)} className="btn-glass size-12 flex items-center justify-center rounded-2xl disabled:opacity-10 hover:border-primary/40 transition-all">
                <ArrowLeft className="size-5" />
              </button>
              <button disabled={page >= totalPages - 1} onClick={() => setPage((p) => p + 1)} className="btn-glass size-12 flex items-center justify-center rounded-2xl disabled:opacity-10 hover:border-primary/40 transition-all">
                <ArrowRight className="size-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
